import { forwardRef, cloneElement, type ComponentProps, type ComponentPropsWithoutRef, type MouseEvent, type ReactElement } from "react";
import { twMerge } from "tailwind-merge";
import Tooltip from "./Tooltip";

// 24×24 button with a 16×16 icon and 4px padding, matching the Design System
// Figma component (round-02 = 7px). Three color variants:
//   - default: transparent, hover bg-stone-100 (action affordances inside menus / rows)
//   - outline: white surface + stone-200 border + drop shadow (standalone affordances)
//   - primary: filled bronze-600 + white icon (high-emphasis CTA / active state)
const variantClasses = {
    default: {
        button: "bg-transparent hover:bg-stone-100",
        icon: "stroke-stone-600",
        disabled: "hover:bg-transparent cursor-not-allowed opacity-50",
    },
    outline: {
        button:
            "bg-white border border-stone-200 shadow-[0px_4px_12px_-8px_rgba(0,0,0,0.32)] hover:bg-stone-100",
        icon: "stroke-stone-600",
        disabled: "cursor-not-allowed opacity-50",
    },
    primary: {
        button:
            "bg-bronze-600 border border-bronze-500 shadow-[0px_4px_12px_-8px_rgba(0,0,0,0.32)] hover:bg-bronze-700 hover:border-bronze-600",
        icon: "stroke-white",
        disabled: "cursor-not-allowed opacity-50",
    },
} as const;

export type IconButtonVariant = keyof typeof variantClasses;

interface IconButtonProps extends Omit<ComponentPropsWithoutRef<"button">, "onClick" | "type" | "ref"> {
    /** Lucide (or other JSX) icon. Sized + colored per variant; the icon's own className overrides. */
    icon: ReactElement<{ className?: string }>;
    onClick?: (e?: MouseEvent) => void;
    disabled?: boolean;
    variant?: IconButtonVariant;
    /** Size of the button. Defaults to "small". */
    size?: "small" | "medium";
    /** When provided, wraps the button in a Tooltip. Doubles as the default aria-label. */
    tooltip?: string;
    /** Which side the tooltip sits on. Defaults to above the button. */
    tooltipAlign?: ComponentProps<typeof Tooltip>["align"];
    /** Pixels between the tooltip and the button, in the direction it sits. */
    tooltipOffset?: number;
    /** Override the aria-label (defaults to `tooltip` when that's set). */
    ariaLabel?: string;
    className?: string;
    type?: "button" | "submit";
    /** Stops event from bubbling — handy for IconButtons nested inside clickable rows. */
    stopPropagation?: boolean;
    /** Shown in a badge on the top right corner when above 0, such as how many filters are applied. */
    count?: number;
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
    {
        icon,
        onClick,
        disabled,
        variant = "default",
        size = "small",
        tooltip,
        tooltipAlign = "top",
        tooltipOffset = 0,
        ariaLabel,
        className,
        type = "button",
        stopPropagation,
        count,
        ...rest
    },
    ref,
) {
    const v = variantClasses[variant];

    const styledIcon = cloneElement(icon, {
        className: twMerge("h-4 w-4", v.icon, icon.props.className),
    });

    const handleClick = (e: MouseEvent) => {
        if (stopPropagation) e.stopPropagation();
        onClick?.(e);
    };

    const button = (
        <button
            {...rest}
            ref={ref}
            type={type}
            onClick={handleClick}
            disabled={disabled}
            aria-label={ariaLabel ?? tooltip}
            className={twMerge(
                "relative flex shrink-0 items-center justify-center rounded-[7px] p-1 transition-colors focus:outline-none",
                size === "small" && "h-6 w-6",
                size === "medium" && "h-8 w-8",
                v.button,
                disabled && v.disabled,
                className,
            )}
        >
            {styledIcon}
            {count !== undefined && count > 0 && (
                <span className="bg-bronze-600 absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white">
                    {count}
                </span>
            )}
        </button>
    );

    if (tooltip) {
        return (
            <Tooltip text={tooltip} align={tooltipAlign} verticalOffset={tooltipOffset}>
                {button}
            </Tooltip>
        );
    }
    return button;
});

export default IconButton;
